import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { churchToday, isoToPickerDate } from "../care/text";

export const CARE_STATE = {
  active: { label: "Active care", cls: "status-active" },
  regular: { label: "Regular care", cls: "status-neutral" },
  not_monitored: { label: "Not monitored", cls: "status-neutral opacity-70" },
};
export const REL = { head: "Head of household", spouse: "Spouse", child: "Child", parent: "Parent", relative: "Relative", other: "Other" };
export const PRESETS = [14, 30, 45, 60];

export const displayName = (p) => `${p.preferred_name || p.first_name} ${p.last_name}`;

export const CareState = ({ state, testid }) => {
  const s = CARE_STATE[state];
  return s ? <span className={`status ${s.cls}`} data-testid={testid}>{s.label}</span> : null;
};

const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });

export const rhythmText = (r) => {
  if (!r) return null;
  if (r.kind === "follow_up") return { main: `Check-in scheduled for ${fmtDate(r.date)}`, why: "Scheduled follow-up takes priority" };
  if (r.paused) return { main: "Not monitored", why: r.paused === "pco_inactive" ? "Inactive in Planning Center" : "No longer included by Planning Center" };
  if (r.kind === "none") return { main: "No care rhythm", why: "Not monitored" };
  if (r.kind === "active_care") return { main: `Every ${r.days} days`, why: "Active care rhythm" };
  if (r.kind === "group") return { main: `Every ${r.days} days`, why: r.groups_with_rhythm > 1 ? `From ${r.group}, the shortest of ${r.groups_with_rhythm} group rhythms` : `From the ${r.group} group` };
  return { main: `Every ${r.days} days`, why: "Church default rhythm" };
};

export const Rhythm = ({ r, testid, compact }) => {
  const t = rhythmText(r);
  if (!t) return null;
  return compact ? (
    <span className="status status-rhythm" data-testid={testid}>{t.main}</span>
  ) : (
    <div data-testid={testid}><p className="status status-rhythm">{t.main}</p><p className="t-meta mt-1 pl-[15px]" data-testid={`${testid}-why`}>{t.why}</p></div>
  );
};

export const RhythmPicker = ({ value, onChange, allowNone, testid }) => {
  const custom = value && !PRESETS.includes(value);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {allowNone && <button type="button" aria-pressed={!value} onClick={() => onChange(null)} className="choice h-11 px-4" data-testid={`${testid}-none`}>None</button>}
      {PRESETS.map((d) => (
        <button type="button" key={d} aria-pressed={value === d} onClick={() => onChange(d)} className="choice h-11 px-4" data-testid={`${testid}-${d}`}>{d} days</button>
      ))}
      <label className={`choice h-11 px-3 inline-flex items-center gap-2 ${custom ? "border-ink" : ""}`}>
        <span className="text-sm">Custom</span>
        <input type="number" min={1} max={365} value={custom ? value : ""} onChange={(e) => onChange(e.target.value ? Math.max(1, Math.min(365, Number(e.target.value))) : null)} className="w-16 bg-transparent outline-none" placeholder="days" data-testid={`${testid}-custom`} />
      </label>
    </div>
  );
};

const TINTS = ["#E4E2D8", "#DDE5DE", "#E6E1E8", "#DFE4EA", "#ECE4D6"];

export const Initials = ({ p, size = 44 }) => {
  const ini = `${(p.preferred_name || p.first_name)[0]}${p.last_name[0]}`.toUpperCase();
  if (p.photo_url) return <img src={p.photo_url} alt="" className="shrink-0 rounded-full object-cover bg-sunk" style={{ width: size, height: size }} />;
  return (
    <span className="shrink-0 rounded-full text-ink-soft font-serif inline-flex items-center justify-center" style={{ width: size, height: size, fontSize: size * 0.38, background: TINTS[ini.charCodeAt(0) % TINTS.length] }}>
      {ini}
    </span>
  );
};

export const fromPco = (x) => x?.source === "planning_center";

export const SourceTag = ({ x, testid }) => fromPco(x) ? (
  <span className="inline-flex items-center text-[12px] tracking-wide text-ink-faint border border-line rounded-full px-2 py-0.5 font-sans" data-testid={testid}>Planning Center</span>
) : null;

export const Select = ({ value, onChange, children, testid, className = "" }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} className={`field h-12 px-3 ${className}`} data-testid={testid}>{children}</select>
);

export const Field = ({ label, children }) => (
  <label className="block"><span className="t-label block mb-2">{label}</span>{children}</label>
);

export const DatePick = ({ value, onPick, placeholder = "Choose date", testid, className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className={`field h-11 px-3 !w-auto inline-flex items-center gap-2 text-[15px] ${className}`} data-testid={testid}>
        <CalendarIcon size={16} className="text-ink-faint" />{value ? format(new Date(`${value}T12:00:00`), "MMM d, yyyy") : placeholder}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-surface" align="end">
        <Calendar mode="single" selected={value ? new Date(`${value}T12:00:00`) : undefined} defaultMonth={value ? new Date(`${value}T12:00:00`) : isoToPickerDate(churchToday())} disabled={{ before: isoToPickerDate(churchToday()) }}
          onSelect={(d) => { if (d) { onPick(format(d, "yyyy-MM-dd")); setOpen(false); } }} data-testid={`${testid}-calendar`} />
      </PopoverContent>
    </Popover>
  );
};
